// Designer P4 — rich keyframe animation. A node's animation can be a simple
// preset (blink/pulse/spin) or a custom keyframe track with per-keyframe
// transform/opacity, a timing curve (cubic-bezier), iteration/direction,
// and an optional motion-path (offset-path). These helpers turn a spec into
// CSS (@keyframes + the animation shorthand), shared by the canvas runtime
// and mirrored server-side in view-html.ts.

export interface Keyframe { at: number; opacity?: number; x?: number; y?: number; rot?: number; scale?: number }
export interface AnimSpec {
  kind?: 'blink' | 'pulse' | 'spin' | 'custom';
  when?: 'always' | 'active';
  duration?: number;          // seconds
  iterations?: number | 'infinite';
  direction?: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
  timing?: string;            // 'ease' | 'linear' | 'cubic-bezier(...)' …
  mode?: 'keyframes' | 'path';
  keyframes?: Keyframe[];
  offsetPath?: string;        // SVG path data for motion along a path
}

export const SIMPLE_KINDS = ['blink', 'pulse', 'spin'];

/** @keyframes body for a custom spec (keyframe track or motion-path). */
export function customKeyframes(name: string, a: AnimSpec): string {
  if (a.mode === 'path') return `@keyframes ${name}{from{offset-distance:0%}to{offset-distance:100%}}`;
  const kfs = [...(a.keyframes ?? [])].sort((x, y) => x.at - y.at);
  if (!kfs.length) return '';
  const body = kfs.map((k) => {
    const tf: string[] = [];
    if (k.x != null || k.y != null) tf.push(`translate(${k.x ?? 0}px,${k.y ?? 0}px)`);
    if (k.rot != null) tf.push(`rotate(${k.rot}deg)`);
    if (k.scale != null) tf.push(`scale(${k.scale})`);
    const decls: string[] = [];
    if (tf.length) decls.push(`transform:${tf.join(' ')}`);
    if (k.opacity != null) decls.push(`opacity:${k.opacity}`);
    return `${k.at}%{${decls.join(';')}}`;
  }).join('');
  return `@keyframes ${name}{${body}}`;
}

/** The `animation` shorthand + optional `offset-path` for a custom spec. */
export function animShorthand(name: string, a: AnimSpec): { animation: string; offsetPath?: string } {
  const dur = a.duration ?? 2;
  const it = a.iterations == null || a.iterations === 'infinite' ? 'infinite' : a.iterations;
  const dir = a.direction ?? 'normal';
  const timing = a.timing && a.timing.trim() ? a.timing : 'ease';
  return {
    animation: `${name} ${dur}s ${timing} 0s ${it} ${dir}`,
    offsetPath: a.mode === 'path' && a.offsetPath ? `path('${a.offsetPath}')` : undefined,
  };
}

/** A unique, CSS-safe @keyframes name for a node. */
export function animName(nodeId: string): string {
  return 'kf_' + nodeId.replace(/[^a-zA-Z0-9_]/g, '_');
}
